import type { Point } from "../../../../../types/stage-types.js";

export interface SketchViewportConfig {
  readonly width?: number;
  readonly height?: number;
  readonly className?: string;
}

export interface SketchViewportSize {
  readonly width: number;
  readonly height: number;
}

export interface SketchViewportAPI {
  readonly element: SVGSVGElement;
  readonly contentRoot: SVGGElement;
  readonly getLogicalSize: () => SketchViewportSize;
  readonly setLogicalSize: (next: Partial<SketchViewportSize>) => void;
  readonly screenToSketch: (point: Point) => Point;
  readonly destroy: () => void;
}

const DEFAULT_VIEWPORT_WIDTH = 1920;
const DEFAULT_VIEWPORT_HEIGHT = 1080;

/**
 * Create the SVG viewport that the sketch module renders into.
 * Encapsulates layout sizing and coordinate conversion helpers.
 */
export const createSketchViewport = (
  host: HTMLElement,
  config: SketchViewportConfig = {}
): SketchViewportAPI => {
  let logicalWidth =
    typeof config.width === "number" && config.width > 0
      ? config.width
      : DEFAULT_VIEWPORT_WIDTH;
  let logicalHeight =
    typeof config.height === "number" && config.height > 0
      ? config.height
      : DEFAULT_VIEWPORT_HEIGHT;

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.classList.add("canvas-layer__sketch-viewport");
  if (config.className) {
    svg.classList.add(config.className);
  }

  svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  svg.setAttribute("role", "presentation");
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
  svg.setAttribute("tabindex", "-1");

  const contentRoot = document.createElementNS("http://www.w3.org/2000/svg", "g");
  contentRoot.classList.add("canvas-layer__sketch-root");
  svg.appendChild(contentRoot);

  const updateViewport = (): void => {
    svg.setAttribute("viewBox", `0 0 ${logicalWidth} ${logicalHeight}`);
    svg.dataset.viewportWidth = `${logicalWidth}`;
    svg.dataset.viewportHeight = `${logicalHeight}`;
  };

  updateViewport();

  svg.style.width = "100%";
  svg.style.height = "100%";
  svg.style.touchAction = "none";

  host.appendChild(svg);

  const getLogicalSize = (): SketchViewportSize => ({
    width: logicalWidth,
    height: logicalHeight,
  });

  const setLogicalSize = (next: Partial<SketchViewportSize>): void => {
    if (typeof next.width === "number" && next.width > 0) {
      logicalWidth = next.width;
    }

    if (typeof next.height === "number" && next.height > 0) {
      logicalHeight = next.height;
    }

    updateViewport();
  };

  const screenToSketch = (point: Point): Point => {
    const rect = svg.getBoundingClientRect();

    if (rect.width <= 0 || rect.height <= 0) {
      return { x: 0, y: 0 };
    }

    const normalizedX = (point.x - rect.left) / rect.width;
    const normalizedY = (point.y - rect.top) / rect.height;

    return {
      x: normalizedX * logicalWidth,
      y: normalizedY * logicalHeight,
    };
  };

  const destroy = (): void => {
    if (svg.parentElement === host) {
      host.removeChild(svg);
    }
  };

  return {
    element: svg,
    contentRoot,
    getLogicalSize,
    setLogicalSize,
    screenToSketch,
    destroy,
  };
};
